import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { StyleProp } from "react-native"

import { DefaultTheme, useTheme as useNavTheme } from "@react-navigation/native"
import * as SystemUI from "expo-system-ui"

import type { Theme, ThemeContexts, ThemedStyle, ThemedStyleArray } from "src/theme"
import { Alert } from "src/components/Alert"
import { darkTheme, lightTheme } from "src/theme"
import { storage } from "src/utils/storage/mmkv"

const THEME_STORAGE_KEY = "user.theme"

/** The raw user preference, stored in MMKV. "system" means follow the device setting. */
export type ThemePreference = "dark" | "light" | "system"

type ThemeContextType = {
  themeScheme: ThemeContexts
  themePreference: ThemePreference
  setThemeContextOverride: (newTheme: ThemeContexts) => void
}

// create a React context and provider for the current theme
export const ThemeContext = createContext<ThemeContextType>({
  themeScheme: undefined, // default to the system theme
  themePreference: "system",
  setThemeContextOverride: (_newTheme: ThemeContexts) => {
    Alert.alert("setThemeContextOverride not implemented")
  },
})

const themeContextToTheme = (themeContext: ThemeContexts): Theme =>
  themeContext === "dark" ? darkTheme : lightTheme

const setImperativeTheming = (theme: Theme) => {
  SystemUI.setBackgroundColorAsync(theme.colors.background)
}

export const useThemeProvider = (initialTheme: ThemeContexts = undefined) => {
  // Read persisted preference synchronously on first render
  const storedPreference = storage.getString(THEME_STORAGE_KEY) as ThemePreference | undefined
  const resolvedInitial: ThemeContexts =
    storedPreference && storedPreference !== "system" ? storedPreference : initialTheme

  const [overrideTheme, setTheme] = useState<ThemeContexts>(resolvedInitial)
  const [themePreference, setThemePreference] = useState<ThemePreference>(
    storedPreference ?? "system",
  )

  const setThemeContextOverride = useCallback((newTheme: ThemeContexts) => {
    const preference: ThemePreference = newTheme ?? "system"
    setTheme(newTheme)
    setThemePreference(preference)
    storage.set(THEME_STORAGE_KEY, preference)
  }, [])

  const themeScheme = overrideTheme

  useEffect(() => {
    setImperativeTheming(themeContextToTheme(themeScheme ?? "dark"))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overrideTheme])

  return {
    themeScheme,
    themePreference,
    setThemeContextOverride,
    ThemeProvider: ThemeContext.Provider,
  }
}

interface UseAppThemeValue {
  // The theme object from react-navigation
  navTheme: typeof DefaultTheme
  // A function to set the theme context override (for switching modes)
  setThemeContextOverride: (newTheme: ThemeContexts) => void
  // The current theme object
  theme: Theme
  // The current theme context "light" | "dark"
  themeContext: ThemeContexts
  // The raw user preference: "dark" | "light" | "system" (system = follow device)
  themePreference: ThemePreference
  // A function to apply the theme to a style object.
  // See examples in the components directory or read the docs here:
  // https://docs.infinite.red/ignite-cli/boilerplate/app/utils/
  themed: <T>(styleOrStyleFn: ThemedStyle<T> | StyleProp<T> | ThemedStyleArray<T>) => T
}

/**
 * Custom hook that provides the app theme and utility functions for theming.
 *
 * @returns {UseAppThemeReturn} An object containing various theming values and utilities.
 * @throws {Error} If used outside of a ThemeProvider.
 */
export const useAppTheme = (): UseAppThemeValue => {
  const navTheme = useNavTheme()
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider")
  }

  const { themeScheme: overrideTheme, themePreference, setThemeContextOverride } = context

  const themeContext: ThemeContexts = useMemo(
    () => overrideTheme || (navTheme.dark ? "dark" : "light"),
    [overrideTheme, navTheme],
  )

  const themeVariant: Theme = useMemo(() => themeContextToTheme(themeContext), [themeContext])

  const themed = useCallback(
    <T>(styleOrStyleFn: ThemedStyle<T> | StyleProp<T> | ThemedStyleArray<T>) => {
      const flatStyles = [styleOrStyleFn].flat(3) as (ThemedStyle<T> | StyleProp<T>)[]
      const stylesArray = flatStyles.map((f) => {
        if (typeof f === "function") {
          return (f as ThemedStyle<T>)(themeVariant)
        } else {
          return f
        }
      })

      // Flatten the array of styles into a single object
      return Object.assign({}, ...stylesArray) as T
    },
    [themeVariant],
  )

  return {
    navTheme,
    setThemeContextOverride,
    theme: themeVariant,
    themeContext,
    themePreference,
    themed,
  }
}
